'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createDiscoveryCall } from '@/lib/services/crmService';

interface BookingFormData {
  fullName: string;
  email: string;
  phone: string;
  jobTitle: string;
  preferredDate: string;
  preferredTime: string;
  timeZone: string;
  additionalNotes: string;
}

const BookingCalendar = () => {
  const [formData, setFormData] = useState<BookingFormData>({
    fullName: '',
    email: '',
    phone: '',
    jobTitle: '',
    preferredDate: '',
    preferredTime: '',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    additionalNotes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await createDiscoveryCall({
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company_name: '',
        job_title: formData.jobTitle,
        preferred_date: formData.preferredDate,
        preferred_time: formData.preferredTime,
        time_zone: formData.timeZone,
        additional_notes: formData.additionalNotes,
      });

      setIsBooked(true);
    } catch (err) {
      setError('Failed to book discovery call. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isBooked) {
    return (
      <section id="booking-calendar" className="py-20 lg:py-32 bg-gradient-to-br from-success/5 to-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-8">
              <Icon name="CheckCircleIcon" size={64} variant="solid" className="text-success" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-headline text-primary mb-6">
              Discovery Call Confirmed!
            </h2>
            <p className="text-xl text-muted-foreground font-body mb-8">
              Thank you for booking your discovery call. You'll receive a confirmation email with:
            </p>
            <div className="bg-card rounded-xl p-8 shadow-strong text-left mb-8">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Icon name="CalendarIcon" size={24} variant="solid" className="text-primary flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-foreground mb-1">Calendar Invitation</div>
                    <div className="text-sm text-muted-foreground">Compatible with Google Calendar, Outlook, and Apple Calendar</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="DocumentTextIcon" size={24} variant="solid" className="text-primary flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-foreground mb-1">Pre-Call Questionnaire</div>
                    <div className="text-sm text-muted-foreground">Help us personalize your consultation experience</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="BookOpenIcon" size={24} variant="solid" className="text-primary flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-foreground mb-1">Preparation Materials</div>
                    <div className="text-sm text-muted-foreground">Resources to maximize your discovery call value</div>
                  </div>
                </li>
              </ul>
            </div>
            <p className="text-muted-foreground font-body">
              We look forward to discussing your AI transformation journey!
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking-calendar" className="py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary mb-6">
              Schedule Your Discovery Call
            </h2>
            <p className="text-xl text-muted-foreground font-body">
              Choose a time that works for you. All times are shown in your local time zone.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-8 lg:p-12 shadow-strong">
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
                <Icon name="ExclamationCircleIcon" size={24} variant="solid" className="text-destructive flex-shrink-0" />
                <p className="text-destructive font-body">{error}</p>
              </div>
            )}

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-semibold text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    placeholder="john.smith@company.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-semibold text-foreground mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    placeholder="Chief Digital Officer"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-semibold text-foreground mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-semibold text-foreground mb-2">
                    Preferred Time *
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select time</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="timeZone" className="block text-sm font-semibold text-foreground mb-2">
                  Time Zone
                </label>
                <input
                  type="text"
                  id="timeZone"
                  name="timeZone"
                  value={formData.timeZone}
                  onChange={handleChange}
                  readOnly
                  className="w-full px-4 py-3 rounded-lg border border-border bg-muted text-muted-foreground cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="additionalNotes" className="block text-sm font-semibold text-foreground mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                  placeholder="Any specific topics you'd like to discuss?"
                />
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-semibold font-cta text-base text-white bg-secondary hover:bg-secondary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-all duration-300 shadow-strong hover:shadow-xl hover:-translate-y-0.5"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} variant="solid" className="animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    Confirm Discovery Call
                    <Icon name="CheckIcon" size={20} variant="solid" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingCalendar;